import { Fragment, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { presupuestoApi } from '../../utils/api';
import { dinero, rotulosDe, nombresDe, unidadDe } from './formato';

const GRUPOS = [['ingreso', 'Ingresos'], ['costo', 'Costos'], ['gasto', 'Gastos']];

/**
 * Presupuesto contra real, acumulado hasta el mes (o período de 4 semanas) elegido. El real sale de los asientos
 * contabilizados con la misma regla del estado de resultados; el comprometido, del saldo
 * sin pagar de las órdenes de compra. Lo que se gastó sin presupuesto también aparece.
 */
export default function TabEjecucion({ data, verEn = null }) {
  // Por omisión, hasta el período de hoy si el presupuesto lo cubre; si no, el año entero.
  const rotulos = rotulosDe(data); const N = rotulos.length; const nombres = nombresDe(data);
  const hoyCR = new Date(Date.now() - 6 * 3600e3).toISOString().slice(0, 10);
  const iHoy = (data.periodos || []).findIndex(p => p.desde <= hoyCR && hoyCR <= p.hasta);
  const mesDefecto = iHoy >= 0 ? iHoy + 1 : N;
  const [hasta, setHasta] = useState(mesDefecto);
  const [ej, setEj] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [abiertas, setAbiertas] = useState({});
  const [soloDesvios, setSoloDesvios] = useState(false);
  // Si la cabecera pide verlo en la otra moneda, el servidor la calcula así (y lo real no se convierte de ida y vuelta).
  const din = (n, o = {}) => dinero(n, { moneda: ej?.moneda || verEn || data.moneda, cero: '–', ...o });

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    presupuestoApi.ejecucion(data.id, hasta, verEn)
      .then(r => { if (vivo) setEj(r.data.data); })
      .catch(e => toast.error(e.response?.data?.error || 'No se pudo calcular la ejecución'))
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [data.id, hasta, data.estandar.utilidad.total, verEn]);

  if (cargando && !ej) return <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="animate-spin mr-2" /> Calculando…</div>;
  if (!ej) return null;

  const clave = (f) => `${f.cuentaId}|${f.centroCostoId || ''}`;
  const visibles = (tipo) => ej.filas.filter(f => f.tipo === tipo && (!soloDesvios || !f.favorable || f.sinPresupuesto));
  const pct = (v) => (v == null ? '–' : `${v > 0 ? '+' : ''}${v.toLocaleString('es-CR', { maximumFractionDigits: 1 })} %`);
  const sinPres = ej.filas.filter(f => f.sinPresupuesto);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2 text-gray-600">Acumulado de {nombres[0].split(' (')[0]} a
          <select value={hasta} onChange={(e) => setHasta(Number(e.target.value))} className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white" data-prueba="ejecucion-hasta">
            {nombres.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-gray-600"><input type="checkbox" checked={soloDesvios} onChange={(e) => setSoloDesvios(e.target.checked)} /> Solo desvíos desfavorables y gasto sin presupuesto</label>
        {cargando && <Loader2 size={16} className="animate-spin text-gray-400" />}
        <span className="ml-auto text-xs text-gray-500">Real: asientos contabilizados (sin los de cierre){(ej?.moneda || data.moneda) === 'USD' ? `, convertidos con el TC de cada ${unidadDe(data)} del presupuesto` : ''}{ej?.convertido ? `. Lo presupuestado, convertido a ${ej.moneda === 'CRC' ? 'colones' : 'dólares'} con el TC de cada ${unidadDe(data)}` : ''}.</span>
      </div>

      {sinPres.length > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-sm">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{sinPres.length} cuenta(s) tienen movimiento real o comprometido sin presupuesto en el año: {sinPres.slice(0, 4).map(f => `${f.codigo} ${f.cuenta}${f.centro ? ' · ' + f.centro : ''}`).join(', ')}{sinPres.length > 4 ? '…' : ''}</span>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[1200px]">
          <thead className="bg-gray-50 text-[10.5px] font-semibold tracking-wider uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2">Cuenta</th><th className="text-left px-3 py-2">Centro</th>
              <th className="text-right px-3 py-2">Presupuesto</th><th className="text-right px-3 py-2">Real</th>
              <th className="text-right px-3 py-2">Variación</th><th className="text-right px-3 py-2">%</th>
              <th className="text-right px-3 py-2" title="Real hasta el período elegido + lo presupuestado para los que faltan: cómo terminaría el año">Al cierre</th><th className="text-right px-3 py-2" title="Presupuesto del año">Presupuesto año</th>
              <th className="text-right px-3 py-2" title="Órdenes de compra sin pagar, sin IVA">Comprometido</th>
              <th className="text-right px-3 py-2" title="Presupuesto del año − real del año − comprometido">Disponible año</th>
            </tr>
          </thead>
          <tbody>
            {GRUPOS.map(([tipo, titulo]) => {
              const filas = visibles(tipo); const t = ej.totales[`${tipo}s`];
              if (!ej.filas.some(f => f.tipo === tipo)) return null;
              const varT = t.real - t.presupuesto;
              const favT = tipo === 'ingreso' ? varT >= 0 : varT <= 0;
              return (
                <Fragment key={tipo}>
                  <tr className="bg-gray-50/70 font-semibold text-gray-800 border-t border-gray-200">
                    <td className="px-3 py-2" colSpan={2}>{titulo}</td>
                    <td className="px-3 py-2 text-right font-mono">{din(t.presupuesto)}</td><td className="px-3 py-2 text-right font-mono">{din(t.real)}</td>
                    <td className={clsx('px-3 py-2 text-right font-mono', favT ? 'text-emerald-700' : 'text-red-600')}>{din(varT)}</td>
                    <td className="px-3 py-2 text-right font-mono text-gray-500">{pct(t.presupuesto ? varT / Math.abs(t.presupuesto) * 100 : null)}</td>
                    <td className="px-3 py-2 text-right font-mono" data-prueba={`cierre-${tipo}`}>{din(t.proyeccionCierre)}</td><td className="px-3 py-2 text-right font-mono text-gray-500">{din(t.presupuestoAnual)}</td>
                    <td className="px-3 py-2 text-right font-mono">{tipo === 'ingreso' ? '' : din(t.comprometido)}</td><td></td>
                  </tr>
                  {filas.map(f => {
                    const k = clave(f); const abierta = abiertas[k];
                    return (
                      <Fragment key={k}>
                        <tr className="border-t border-gray-100 hover:bg-indigo-50/30 cursor-pointer" onClick={() => setAbiertas({ ...abiertas, [k]: !abierta })}>
                          <td className="px-3 py-1.5"><span className="inline-flex items-center gap-1">{abierta ? <ChevronDown size={14} /> : <ChevronRight size={14} />}<span className="font-mono text-xs text-gray-400">{f.codigo}</span> {f.cuenta}</span>
                            {f.sinPresupuesto && <span className="ml-2 text-[10.5px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">sin presupuesto</span>}</td>
                          <td className="px-3 py-1.5 text-gray-600">{f.centro || ''}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{din(f.presupuesto)}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{din(f.real)}</td>
                          <td className={clsx('px-3 py-1.5 text-right font-mono', f.variacion === 0 ? 'text-gray-400' : f.favorable ? 'text-emerald-700' : 'text-red-600')}>{din(f.variacion)}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-gray-500">{pct(f.variacionPct)}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{din(f.proyeccionCierre)}</td><td className="px-3 py-1.5 text-right font-mono text-gray-500">{din(f.presupuestoAnual)}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-gray-600">{f.tipo === 'ingreso' ? '' : din(f.comprometido)}</td>
                          <td className={clsx('px-3 py-1.5 text-right font-mono', f.disponible != null && f.disponible < 0 ? 'text-red-600 font-semibold' : 'text-gray-700')}>{f.disponible == null ? '' : din(f.disponible, { cero: dinero(0, { moneda: data.moneda }) })}</td>
                        </tr>
                        {abierta && (
                          <tr className="bg-gray-50/50"><td colSpan={10} className="px-3 py-2">
                            <div className="grid text-xs gap-x-1 gap-y-0.5" style={{ gridTemplateColumns: `7rem repeat(${N}, minmax(0, 1fr))` }}>
                              <div />{rotulos.map((m, i) => <div key={m} className={clsx('text-right text-gray-400', i + 1 > hasta && 'opacity-50')}>{m}</div>)}
                              {[['Presupuesto', f.presupuestoMeses], ['Real', f.realMeses], ...(f.tipo !== 'ingreso' && f.comprometido !== 0 ? [['Comprometido', f.comprometidoMeses]] : [])].map(([nombre, meses]) => (
                                <Fragment key={nombre}>
                                  <div className="text-gray-500">{nombre}</div>
                                  {meses.map((v, i) => <div key={i} className={clsx('text-right font-mono truncate', i + 1 > hasta && 'opacity-50')} title={din(v)}>{din(v, { decimales: 0 })}</div>)}
                                </Fragment>
                              ))}
                            </div>
                          </td></tr>
                        )}
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            })}
            <tr className="border-t-2 border-gray-300 font-bold bg-indigo-50/50">
              <td className="px-3 py-2" colSpan={2}>Utilidad</td>
              <td className="px-3 py-2 text-right font-mono">{din(ej.totales.utilidad.presupuesto)}</td>
              <td className="px-3 py-2 text-right font-mono">{din(ej.totales.utilidad.real)}</td>
              <td className={clsx('px-3 py-2 text-right font-mono', ej.totales.utilidad.real >= ej.totales.utilidad.presupuesto ? 'text-emerald-700' : 'text-red-600')}>{din(ej.totales.utilidad.real - ej.totales.utilidad.presupuesto)}</td>
              <td></td><td className="px-3 py-2 text-right font-mono" data-prueba="cierre-utilidad">{din(ej.totales.utilidad.proyeccionCierre)}</td><td className="px-3 py-2 text-right font-mono text-gray-500">{din(ej.totales.utilidad.presupuestoAnual)}</td>
              <td colSpan={2}></td>
            </tr>
            {ej.filas.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-gray-400">Sin presupuesto imputado a cuentas ni movimiento real en {data.anio}.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
